import html2canvas from "html2canvas";
import { useContext, useState } from "react";
import { Context } from "../context";

interface UsePrintImagesProps {
    title?: string;
}

interface UsePrintImagesReturn {
    handlePrint: (refs: any[]) => Promise<void>;
    isGenerating: boolean;
}

export const usePrintImages = ({ title = "Document" }: UsePrintImagesProps): UsePrintImagesReturn => {
    const [isGenerating, setIsGenerating] = useState(false);
    const { setLoading } = useContext(Context);

    const handlePrint = async (refs: React.RefObject<HTMLElement>[]) => {
        try {
            setIsGenerating(true);
            setLoading?.((prev) => prev + 1);

            const elements = refs
                .map((ref) => ref.current)
                .filter(
                    (el): el is HTMLElement => !!el && el.offsetHeight > 0 && el.offsetWidth > 0 && el.innerHTML.trim() !== ""
                );

            if (elements.length === 0) throw new Error("No valid elements found");

            const canvases = await Promise.all(
                elements.map((el) =>
                    html2canvas(el, {
                        scale: 3,
                        useCORS: true,
                        allowTaint: false,
                        logging: false,
                        backgroundColor: "#ffffff",
                    })
                )
            );

            const imagesHtml = canvases
                .map((canvas, index) => {
                    const imgData = canvas.toDataURL("image/png", 1.0);
                    return `
                        <div class="page">
                            <img src="${imgData}" alt="Page ${index + 1}" />
                        </div>`;
                })
                .join("");

            const iframe = document.createElement("iframe");
            iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;";
            document.body.appendChild(iframe);

            const doc = iframe.contentDocument;
            if (!doc) throw new Error("Cannot access iframe document");

            doc.open();
            doc.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>${title}</title>
                    <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body { background: white; }
                        .page {
                            width: 100%;
                            page-break-after: always;
                            display: flex;
                            justify-content: center;
                            align-items: flex-start;
                        }
                        .page:last-child { page-break-after: avoid; }
                        img {
                            width: 100%;
                            height: auto;
                            display: block;
                        }
                        @media print {
                            body { margin: 0; }
                            @page {
                                size: A4;
                                margin: 10mm;
                            }
                        }
                    </style>
                </head>
                <body>${imagesHtml}</body>
                </html>
            `);
            doc.close();

            iframe.onload = () => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                    setIsGenerating(false);
                    setLoading?.((prev) => prev - 1);
                }, 1000);
            };
        } catch (error) {
            console.error("Print failed:", error);
            setIsGenerating(false);
            setLoading?.((prev) => prev - 1);
        }
    };

    return { handlePrint, isGenerating };
};